import { useState, useMemo, useEffect } from "react";
import { Button, CommonModal, Loader } from "@/common/components/ui";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import { useNavigate } from "react-router-dom";
import navigationRoutes from "@/utils/navigationRoutes";
import { PersonalDetailsPayload } from "@/utils/types/NPS/Stepper/PersonalDetails";
import PersonalDetailsService from "@/services/NPS/Stepper/APIs/PersonalDetailsService";
import RegistrationDetailsValidateService from "@/services/NPS/Stepper/APIs/RegistrationDetailsValidateService";

const formatAmount = (val: number | string): string => {
  const num = Number(val);
  if (Number.isInteger(num)) {
    return `₹${num}`;
  }
  return `₹${num.toString()}`;
};

const PaymentPranNoGeneration: React.FC<{
  ackId: string;
  nextStep: string;
  initialDetails: any;
  onNext: () => void;
  onPrev: () => void;
  setNextStep: (nextStep: string) => void;
  setInitialDetails: (initialDetails: any) => void;
  registrationDetails?: Registration | null;
  setIsPaymentSuccessModalClose: (isPaymentSuccessModalClose: boolean) => void;
  isPaymentSuccessModalClose: boolean;
}> = ({ ackId, registrationDetails, onPrev, isPaymentSuccessModalClose }) => {
  const navigate = useNavigate();
  const [amountInput, setAmountInput] = useState("₹1000");
  const [amountError, setAmountError] = useState("");
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paypointzPaymentId, setPaypointzPaymentId] = useState<string | null>(
    null
  );
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState<string[]>([]);
  // const { tenantId } = useTenant();
  // const epranMode = registrationDetails?.personalDtls?.epranWelcomePran ?? "1";
  // const getKitCharge = (mode: string): number => {
  //   switch (mode) {
  //     case "1":
  //       return 18;
  //     case "2":
  //       return 35;
  //     case "3":
  //       return 40;
  //     default:
  //       return 18;
  //   }
  // };

  useEffect(() => {
    if (
      registrationDetails?.personalDtls &&
      Object.keys(registrationDetails?.personalDtls).length > 0
    ) {
      const raw =
        registrationDetails?.personalDtls?.pran_total_contribution ?? 1000;
      setAmountInput(formatAmount(raw));
      setPaypointzPaymentId(
        registrationDetails?.personalDtls?.paypointz_payment ?? null
      );
    }
  }, [registrationDetails, paypointzPaymentId, isPaymentSuccessModalClose]);

  // const kitCharge = getKitCharge(epranMode);
  const contributionAmount = useMemo(() => {
    const num = parseFloat(amountInput.replace(/[^0-9.]/g, ""));
    return isNaN(num) ? 1000 : num;
  }, [amountInput]);
  const accountOpening = 200;
  const gst = parseFloat((accountOpening * 0.18).toFixed(2));
  const totalPayable = parseFloat(
    (contributionAmount + accountOpening + gst).toFixed(2)
  );

  const formatCurrency = (value: number): string => {
    return `₹${value.toFixed(2)}`;
  };

  const handleSummary = async () => {
    const numericValue = parseFloat(amountInput.replace(/[^0-9.]/g, ""));
    // 🔴 Validation before continuing
    if (!numericValue || isNaN(numericValue) || numericValue < 500) {
      setAmountError("Minimum contribution amount must be ₹500 or more");
      return;
    }
    setAmountError("");
    setIsLoading(true);
    const payload: PersonalDetailsPayload = {
      pran_total_contribution: contributionAmount,
      pran_ac_opening_charges: accountOpening,
      pran_ac_opening_charges_gst: Number((accountOpening * 0.18).toFixed(2)),
      total_payable_amount: Number(totalPayable.toFixed(2)),
    };
    try {
      const response = await PersonalDetailsService.updatePersonalDetailsById(
        registrationDetails?.initialDetails?.ackId ?? "",
        payload
      );
      if (response.success) {
        const response =
          await RegistrationDetailsValidateService.validateRegistrationDetails(
            ackId
          );
        if (response?.success === true && response?.valid === true) {
          navigate(navigationRoutes.SUBSCRIBER_REGISTRATION_SUMMARY, {
            state: { registrationDetails },
          });
        } else {
          setIsLoading(false);
          const fieldErrors = response?.errors || [];
          console.log("fieldErrors", fieldErrors);
          setErrorModalData(fieldErrors);
          setOpenErrorModal(true);
        }

        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to save payment details", error);
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    onPrev();
  };

  const handleTermsAndConditions = () => {
    const url = `${window.location.origin}/terms-and-conditions`;
    window.open(url, "_blank");
  };

  const ErrorModal = () => {
    return (
      <CommonModal
        isOpen={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
      >
        <div className="p-4">
          <h3 className="font-sans text-[20px] font-semibold text-red-600">
            Validation Error
          </h3>
          <div className="mt-3">
            <ul className="list-disc pl-5">
              {errorModalData.map(error => (
                <li key={error} className="text-red-600">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CommonModal>
    );
  };

  return (
    <>
      {isLoading && <Loader message="Checking payment status..." />}
      <div className="space-y-10 rounded-2xl bg-white py-10">
        <h2 className="font-sans text-[20px] font-semibold text-[#127B8D] sm:text-2xl md:text-[26px]">
          Payment & PRAN No. Generation
        </h2>

        <div>
          <h3 className="mb-3 font-sans text-[20px] font-semibold text-[#000]">
            Payment Details
          </h3>

          <div className="max-w-xl">
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              First Time Tier I Contribution Amount*
            </label>

            <input
              type="text"
              value={amountInput}
              onChange={e => {
                const value = e.target.value;
                if (!/^₹?[0-9]*\.?[0-9]*$/.test(value)) {
                  return;
                }

                setAmountInput(value);
                const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
                if (!value.trim() || isNaN(numericValue)) {
                  setAmountError("Minimum contribution amount is ₹500");
                  return;
                }
                if (numericValue < 500) {
                  setAmountError(
                    "Minimum contribution amount must be ₹500 or more"
                  );
                } else {
                  setAmountError("");
                }
              }}
              className={`mt-1 w-full rounded-md border px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD] ${amountError ? "border-red-500" : "border-[#DFE7EC]"}`}
              placeholder="₹1000"
            />

            {amountError && (
              <p className="mt-1 text-sm text-red-600">{amountError}</p>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-[#E6FFFA]">
          <div className="grid grid-cols-2 bg-[#E4FFFB] px-4 py-3 text-sm font-semibold text-gray-700 sm:text-base">
            <span className="font-sans text-[18px] font-semibold text-[#707070]">
              Payment Summary
            </span>
            <span className="text-right font-sans text-[18px] font-semibold text-[#707070]">
              Contribution & Charges
            </span>
          </div>

          <div className="grid grid-cols-2 border-b border-gray-100 px-4 py-3">
            <span className="font-sans text-[18px] font-semibold text-[#1D2C56]">
              PRAN Total Contribution
            </span>
            <span className="text-right font-sans text-[18px] font-semibold text-[#2F363F]">
              {formatCurrency(contributionAmount)}
            </span>
          </div>

          <div className="grid grid-cols-2 border-b border-gray-100 px-4 py-3">
            <span className="font-sans text-[18px] font-semibold text-[#1D2C56]">
              PRAN Account Opening Charges
            </span>
            <span className="text-right font-sans text-[18px] font-semibold text-[#2F363F]">
              {formatCurrency(accountOpening)}
            </span>
          </div>

          <div className="grid grid-cols-2 border-b border-gray-100 px-4 py-3">
            <span className="font-sans text-[18px] font-semibold text-[#1D2C56]">
              PRAN Account Opening Charges (GST 18%)
            </span>
            <span className="text-right font-sans text-[18px] font-semibold text-[#2F363F]">
              {formatCurrency(accountOpening * 0.18)}
            </span>
          </div>

          <div className="grid grid-cols-2 px-4 py-4 text-lg font-bold text-[#0C3B5E]">
            <span className="font-sans text-[18px] font-semibold text-[#1D2C56]">
              Total Payable Amount
            </span>
            <span className="text-right font-sans text-[18px] font-semibold text-[#2F363F]">
              {formatCurrency(totalPayable)}
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            checked={isTermsAccepted}
            onChange={e => setIsTermsAccepted(e.target.checked)}
            className="h-5 w-5 accent-[#000]"
          />
          <button
            onClick={handleTermsAndConditions}
            className="cursor-pointer font-sans text-[18px] font-semibold text-[#707070] hover:underline"
          >
            Accept terms and condition
          </button>
        </div>

        <div className="mt-8 flex justify-end gap-4">
          {/* <Button
            type="button"
            onClick={handleSummary}
            className="h-[50px] rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] px-6 py-2 font-sans text-[18px] font-semibold text-white transition hover:opacity-95"
          >
            Summary
          </Button> */}
          <button
            type="button"
            onClick={handleBack}
            className="h-[50px] rounded-md bg-[#F5F5F5] px-8 py-2 font-sans text-[18px] font-semibold text-[#2F363F] hover:bg-[#f5f5f5] hover:opacity-95"
          >
            Back
          </button>

          <Button
            disabled={!isTermsAccepted}
            type="button"
            onClick={handleSummary}
            className={`ounded-md h-[50px] w-[180px] rounded-md py-2 font-sans text-[18px] font-semibold tracking-[1px] text-white ${
              isTermsAccepted
                ? "cursor-pointer bg-gradient-to-r from-[#127B8D] to-[#10B981] hover:opacity-95"
                : "cursor-not-allowed bg-[rgba(112,112,112,0.3)] text-[#2F363F]"
            }`}
          >
            Preview
          </Button>
        </div>
      </div>
      {openErrorModal && <ErrorModal />}
    </>
  );
};

export default PaymentPranNoGeneration;
